import { useState } from "react";
import { z } from "zod";

import { LoginPage } from "@/modules/auth/pages/LoginPage";
import { useLoginMutation } from "@/modules/auth/hooks/useLoginMutation";
import type { LoginFormFields } from "@/modules/auth/schemes";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { LoginError } from "@/modules/auth/lib/LoginError";

export const Route = createFileRoute("/auth/login")({
	validateSearch: (search) =>
		z
			.object({
				redirectTo: z.string().optional(),
			})
			.parse(search),
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation("auth");
	const router = useRouter();
	const search = Route.useSearch();
	const loginMutation = useLoginMutation();
	const [error, setError] = useState<string | null>(null);

	const handleLogin = async (data: LoginFormFields) => {
		setError(null);

		try {
			await loginMutation.mutateAsync(data);
			await router.navigate({
				to: search.redirectTo ?? "/m/dashboard",
			});
		} catch (err) {
			if (err instanceof LoginError && err.code === "account_disabled") {
				setError(t("login.errors.accountDisabled"));
				return;
			}

			setError(t("login.errors.invalidCredentials"));
		}
	};

	return <LoginPage error={error} onLogin={handleLogin} />;
}
